// Teacher Assignments: 2-phase create (no targets in create), assign via /targets API, details drawer with targets

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card, CardContent } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Textarea } from "../../components/ui/Textarea";
import { Modal } from "../../components/ui/Modal";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/Table";
import {
  BookOpen,
  Library,
  Mic,
  Clock,
  Download,
  MessageCircle,
  Users,
  Eye,
  PlusCircle,
  LayoutGrid,
  List,
  UserPlus,
  Target,
  X,
  Pencil,
  Trash2,
  Check,
  Plus,
  History,
  FileText,
  RefreshCw,
  Upload,
  KeyRound,
} from "lucide-react";
import { clsx } from "clsx";
import {
  createAssignment,
  updateAssignment,
  deleteAssignment,
  listAssignments,
  getAssignment,
  uploadAssignmentAttachment,
  deleteAssignmentAttachment,
  uploadAnswerKeyAttachment,
  deleteAnswerKeyAttachment,
  openAssignmentAttachmentFile,
  createTarget,
  listTargets,
  updateTarget,
  deleteTarget,
  listRecentSubmissionsGrouped,
  pickValidationStudentScore,
  type AssignmentDTO,
  type AssignmentTargetDTO,
  type SubmissionDTO,
} from "../../modules/learning/api/assignments";
import {
  createTopic,
  listTopicsForTeacher,
  type CommunicationTopicDTO,
} from "../../modules/learning/api/communicationTopics";
import { TeacherCommunicationPracticeTasksPanel } from "./TeacherCommunicationPracticeTasksPanel";
import { ROUTES, teacherSubmissionReviewPath } from "../../utils/constants";
import {
  getTeacherMyClasses,
  getTeacherMySections,
  getTeacherMyStudents,
  type TeacherClass,
  type TeacherSectionItem,
  type TeacherStudentEntry,
} from "../../modules/roster/api/teacher.client";
import { listClasses, listStudents } from "../../modules/roster/api/roster.client";
import { useAuth } from "../../contexts/AuthContext";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";

const TYPE_OPTIONS = [
  { value: "Story", label: "Story" },
  { value: "Letters", label: "Letters" },
  { value: "Words", label: "Words" },
  { value: "Sentences", label: "Sentences" },
  { value: "Writing", label: "Writing" },
];

const GRADE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((g) => ({
  value: String(g),
  label: `Grade ${g}`,
}));

const CREATE_DISABLED_TOOLTIP = "No classes available to create an assignment";
const CREATE_TOPIC_DISABLED_TOOLTIP = "No classes available to create a topic";

/** Accepted MIME/extensions for teacher assignment materials (aligned with learning-service). */
const ASSIGNMENT_RESOURCE_ACCEPT =
  "video/*,audio/*,image/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.mp4,.webm,.mov,.mkv,.m4v,.mp3,.wav,.m4a,.aac,.ogg,.opus,.flac,.webp,.gif,.bmp,.svg";

/** Format due_date for display or export; returns "—" for invalid or empty values. */
function formatDueDate(value: string | null | undefined): string {
  if (value == null || String(value).trim() === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

type AssignTab = "class" | "students";

const resetCreateForm = () => ({
  title: "",
  titleTamil: "",
  grade: "",
  type: "",
  studentInstructions: "",
  aiInstructions: "",
  answerKeyText: "",
  dueDate: "",
});

type ViewMode = "browse" | "track" | "topics" | "recent";

export const AssignmentsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const assignLocation = useLocation();
  const isAdmin = hasRole("SCHOOL_ADMIN") || hasRole("DEV_TEAM");

  const [viewMode, setViewMode] = useState<ViewMode>("browse");
  const [assignments, setAssignments] = useState<AssignmentDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignLoading, setAssignLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [gradeFilter, setGradeFilter] = useState<string>("all");
  const [assigningAssignment, setAssigningAssignment] = useState<AssignmentDTO | null>(null);
  const [assignTab, setAssignTab] = useState<AssignTab>("class");
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState(resetCreateForm);
  const [createPhase, setCreatePhase] = useState<1 | 2>(1);
  const [createdAssignmentId, setCreatedAssignmentId] = useState<string | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<AssignmentDTO | null>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [pendingTeacherAttachments, setPendingTeacherAttachments] = useState<File[]>([]);
  const [pendingAnswerKeyFile, setPendingAnswerKeyFile] = useState<File | null>(null);
  const [teacherAttachmentBusy, setTeacherAttachmentBusy] = useState(false);
  const [answerKeyAttachmentBusy, setAnswerKeyAttachmentBusy] = useState(false);

  const [editTargetModalOpen, setEditTargetModalOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState<AssignmentTargetDTO | null>(null);
  const [editTargetStudentIds, setEditTargetStudentIds] = useState<Set<string>>(new Set());
  const [updateTargetLoading, setUpdateTargetLoading] = useState(false);

  const [topics, setTopics] = useState<CommunicationTopicDTO[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [createTopicModalOpen, setCreateTopicModalOpen] = useState(false);
  const [createTopicLoading, setCreateTopicLoading] = useState(false);
  const [topicForm, setTopicForm] = useState({ title: "", description: "", section_id: "", grade: "2" });

  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [sections, setSections] = useState<TeacherSectionItem[]>([]);
  const [students, setStudents] = useState<TeacherStudentEntry[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentClassFilter, setStudentClassFilter] = useState<string>("");

  const [detailAssignment, setDetailAssignment] = useState<AssignmentDTO | null>(null);
  const [detailTargets, setDetailTargets] = useState<AssignmentTargetDTO[]>([]);
  const [detailTargetsLoading, setDetailTargetsLoading] = useState(false);
  /** Assignment IDs that have at least one target (used to disable Assign button on cards) */
  const [assignmentIdsWithTargets, setAssignmentIdsWithTargets] = useState<Set<string>>(new Set());
  /** Recent submissions for "Recent Assignments" view - from GET /v1/learning/submissions/recent?limit=30 */
  const [recentSubmissionsList, setRecentSubmissionsList] = useState<SubmissionDTO[]>([]);
  const [loadingRecentSubmissions, setLoadingRecentSubmissions] = useState(false);
  const [recentGradeFilter, setRecentGradeFilter] = useState<string>("all");

  const [deleteConfirmAssignment, setDeleteConfirmAssignment] = useState<AssignmentDTO | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useLayoutEffect(() => {
    const st = (assignLocation.state ?? {}) as { restoreAssignmentsView?: ViewMode };
    const v = st.restoreAssignmentsView;
    if (v === "browse" || v === "track" || v === "topics" || v === "recent") {
      setViewMode(v);
      navigate(`${assignLocation.pathname}${assignLocation.search}`, { replace: true, state: {} });
      return;
    }
    const params = new URLSearchParams(assignLocation.search);
    if (params.get("open") === "communication" || params.get("comm_tasks") === "1") {
      setViewMode("topics");
      if (params.get("comm_tasks") === "1") {
        params.delete("comm_tasks");
        params.set("open", "communication");
        const q = params.toString();
        navigate({ pathname: assignLocation.pathname, search: q ? `?${q}` : "" }, { replace: true });
      }
    }
  }, [assignLocation.pathname, assignLocation.search, assignLocation.state, navigate]);

  useEffect(() => {
    if (viewMode === "topics") return;
    const p = new URLSearchParams(assignLocation.search);
    if (p.get("open") !== "communication" && p.get("comm_tasks") !== "1") return;
    p.delete("open");
    p.delete("comm_tasks");
    const q = p.toString();
    navigate({ pathname: assignLocation.pathname, search: q ? `?${q}` : "", hash: "" }, { replace: true });
  }, [viewMode, assignLocation.pathname, assignLocation.search, navigate]);

  useEffect(() => {
    if (viewMode !== "topics") return;
    const openComm = new URLSearchParams(assignLocation.search).get("open") === "communication";
    const hashPractice = assignLocation.hash === "#teacher-communication-practice";
    if (!openComm && !hashPractice) return;
    requestAnimationFrame(() => {
      document.getElementById("teacher-communication-practice")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [viewMode, assignLocation.search, assignLocation.hash]);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAssignments({ limit: 100 });
      setAssignments(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load assignments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const loadTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const res = await listTopicsForTeacher({ limit: 100 });
      setTopics(res.items);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load communication topics.");
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "topics") loadTopics();
  }, [viewMode, loadTopics]);

  const loadClasses = useCallback(async () => {
    try {
      if (isAdmin) {
        const c = await listClasses({ limit: 100 });
        setClasses(c);
      } else {
        const c = await getTeacherMyClasses();
        setClasses(c);
      }
    } catch {
      setClasses([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const loadSections = useCallback(async () => {
    try {
      const res = await getTeacherMySections({ limit: 100 });
      setSections(Array.isArray(res) ? res : []);
    } catch {
      setSections([]);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "topics" || createTopicModalOpen) loadSections();
  }, [viewMode, createTopicModalOpen, loadSections]);

  const sectionOptions = useMemo(() => {
    return [
      { value: "", label: "All sections" },
      ...sections.map((s) => ({ value: s.section.id, label: s.section.name ?? s.section.id })),
    ];
  }, [sections]);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    try {
      if (isAdmin) {
        const s = await listStudents({ limit: 200 });
        setStudents(
          s.map((st) => ({
            student: {
              id: st.id,
              platform_user_id: st.platform_user_id,
              display_name: st.display_name,
              first_name: st.first_name,
              last_name: st.last_name,
              admission_no: st.admission_no,
              status: st.status,
            },
            class: { id: "", name: "", grade: st.grade },
            section: null,
            enrollment: { id: "", status: "" },
          }))
        );
      } else {
        const s = await getTeacherMyStudents();
        setStudents(Array.isArray(s) ? s : []);
      }
    } catch {
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, [isAdmin]);

  const assignModalOpen = !!assigningAssignment || (createPhase === 2 && !!createdAssignmentId);
  useEffect(() => {
    if (assignModalOpen) loadStudents();
  }, [assignModalOpen, loadStudents]);

  const gradeOptions = useMemo(() => {
    const grades = Array.from(new Set(assignments.map((a) => a.grade))).sort();
    return [
      { value: "all", label: "All Grades" },
      ...grades.map((g) => ({ value: g, label: `Grade ${g}` })),
    ];
  }, [assignments]);

  const filteredAssignments = useMemo(() => {
    if (gradeFilter === "all") return assignments;
    return assignments.filter((a) => a.grade === gradeFilter);
  }, [assignments, gradeFilter]);

  const hasClasses = classes.length > 0;

  const loadRecentSubmissions = useCallback(async () => {
    setLoadingRecentSubmissions(true);
    try {
      const res = await listRecentSubmissionsGrouped(30);
      const items = res.items ?? [];
      setRecentSubmissionsList(items);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load recent submissions.");
      setRecentSubmissionsList([]);
    } finally {
      setLoadingRecentSubmissions(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "recent") loadRecentSubmissions();
  }, [viewMode, loadRecentSubmissions]);

  useEffect(() => {
    if (viewMode === "recent") loadStudents();
  }, [viewMode, loadStudents]);

  /** Map student_id (platform_user_id) -> display name for Recent submissions list */
  const studentNameMap = useMemo(() => {
    const m = new Map<string, string>();
    students.forEach((entry) => {
      const st = entry.student;
      if (!st) return;
      const pid = st.platform_user_id;
      const name =
        st.display_name ||
        [st.first_name, st.last_name].filter(Boolean).join(" ").trim() ||
        pid ||
        st.id ||
        "";
      if (pid) m.set(pid, name);
      if (st.id && st.id !== pid) m.set(st.id, name);
    });
    return m;
  }, [students]);

  /** Latest-first list for Recent Assignments view */
  const sortedRecentSubmissions = useMemo(() => {
    return [...recentSubmissionsList].sort((a, b) => {
      const ta = new Date(a.submitted_at || a.created_at || 0).getTime();
      const tb = new Date(b.submitted_at || b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [recentSubmissionsList]);

  const recentSubmissionGradeOptions = useMemo(() => {
    const grades = Array.from(
      new Set(recentSubmissionsList.map((s) => s.grade_level).filter(Boolean))
    ).sort();
    return [
      { value: "all", label: "All Grades" },
      ...grades.map((g) => ({ value: String(g), label: `Grade ${g}` })),
    ];
  }, [recentSubmissionsList]);

  const filteredRecentSubmissions = useMemo(() => {
    if (recentGradeFilter === "all") return sortedRecentSubmissions;
    return sortedRecentSubmissions.filter((s) => String(s.grade_level) === recentGradeFilter);
  }, [sortedRecentSubmissions, recentGradeFilter]);

  const classOptions = useMemo(
    () => [
      { value: "", label: "Select a class..." },
      ...classes.map((c) => ({ value: c.id, label: c.name + (c.grade ? ` (Grade ${c.grade})` : "") })),
    ],
    [classes]
  );

  const classById = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);

  /** Dynamic grade options for Create form: only from teacher's assigned classes (GET /roster/teacher/me/classes) */
  const createFormGradeOptions = useMemo(() => {
    const grades = new Set<string>();
    classes.forEach((c) => {
      if (c.grade != null && String(c.grade).trim() !== "") grades.add(String(c.grade).trim());
    });
    const sorted = Array.from(grades).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return [
      { value: "", label: "Select grade..." },
      ...sorted.map((g) => ({ value: g, label: `Grade ${g}` })),
    ];
  }, [classes]);

  const studentsWithPlatformId = useMemo(
    () => students.filter((s) => s.student.platform_user_id),
    [students]
  );

  const filteredStudents = useMemo(() => {
    let list = studentsWithPlatformId;
    if (studentClassFilter) {
      list = list.filter((s) => s.class?.id === studentClassFilter);
    }
    if (studentSearch.trim()) {
      const q = studentSearch.trim().toLowerCase();
      list = list.filter(
        (s) =>
          (s.student.display_name || "").toLowerCase().includes(q) ||
          (s.student.first_name || "").toLowerCase().includes(q) ||
          (s.student.last_name || "").toLowerCase().includes(q) ||
          (s.student.admission_no || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [studentsWithPlatformId, studentClassFilter, studentSearch]);

  const openAssignModal = (assignment: AssignmentDTO) => {
    setAssigningAssignment(assignment);
    setAssignTab("class");
    setSelectedClass("");
    setSelectedStudentIds(new Set());
  };

  const closeAssignModal = () => {
    setAssigningAssignment(null);
  };

  const assignmentIdForTarget =
    assigningAssignment?.assignment_id ?? (createPhase === 2 ? createdAssignmentId : null);

  const handleAssignNow = async () => {
    const aid = assignmentIdForTarget;
    if (!aid) return;

    if (assignTab === "class") {
      if (!selectedClass) {
        showErrorToast("Please select a class.");
        return;
      }
      setAssignLoading(true);
      try {
        await createTarget(aid, {
          target_type: "class",
          class_id: selectedClass,
        });
        showSuccessToast("Assignment assigned to class.");
        setAssignmentIdsWithTargets((prev) => new Set([...prev, aid]));
        setSelectedClass("");
        loadAssignments();
        if (detailAssignment?.assignment_id === aid) {
          const res = await listTargets(aid);
          setDetailTargets(res.items);
        }
        if (createPhase === 2) handleCreatePhase2Done();
        else closeAssignModal();
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : "Failed to assign.");
      } finally {
        setAssignLoading(false);
      }
      return;
    }

    if (assignTab === "students") {
      if (selectedStudentIds.size === 0) {
        showErrorToast("Please select at least one student.");
        return;
      }
      const studentList = studentsWithPlatformId.filter((s) =>
        s.student.platform_user_id ? selectedStudentIds.has(s.student.platform_user_id) : false
      );
      const platformIds = studentList
        .map((s) => s.student.platform_user_id)
        .filter((id): id is string => !!id);
      if (platformIds.length === 0) {
        showErrorToast("Selected students have no platform user ID.");
        return;
      }
      setAssignLoading(true);
      try {
        await createTarget(aid, {
          target_type: "students",
          students: platformIds.map((platform_user_id) => ({ platform_user_id })),
        });
        showSuccessToast("Assignment assigned to students.");
        setAssignmentIdsWithTargets((prev) => new Set([...prev, aid]));
        setSelectedStudentIds(new Set());
        loadAssignments();
        if (detailAssignment?.assignment_id === aid) {
          const res = await listTargets(aid);
          setDetailTargets(res.items);
        }
        if (createPhase === 2) handleCreatePhase2Done();
        else closeAssignModal();
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : "Failed to assign.");
      } finally {
        setAssignLoading(false);
      }
    }
  };

  const openCreateModal = () => {
    if (classes.length === 0) return;
    setCreateForm(resetCreateForm());
    setPendingTeacherAttachments([]);
    setPendingAnswerKeyFile(null);
    setCreatePhase(1);
    setCreatedAssignmentId(null);
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setCreateForm(resetCreateForm());
    setPendingTeacherAttachments([]);
    setPendingAnswerKeyFile(null);
    setCreatePhase(1);
    setCreatedAssignmentId(null);
  };

  const openEditModal = (assignment: AssignmentDTO) => {
    setCreateForm({
      title: assignment.title ?? "",
      titleTamil: assignment.title_tamil ?? "",
      studentInstructions: assignment.instructions ?? "",
      aiInstructions: assignment.ai_instructions ?? "",
      answerKeyText: assignment.answer_key_text ?? "",
      grade: assignment.grade ?? "",
      type: assignment.type ?? "",
      dueDate: assignment.due_date ? String(assignment.due_date).slice(0, 10) : "",
    });
    setEditingAssignment(assignment);
    setEditModalOpen(true);
    void (async () => {
      try {
        const fresh = await getAssignment(assignment.assignment_id);
        setEditingAssignment(fresh);
        setCreateForm({
          title: fresh.title ?? "",
          titleTamil: fresh.title_tamil ?? "",
          studentInstructions: fresh.instructions ?? "",
          aiInstructions: fresh.ai_instructions ?? "",
          answerKeyText: fresh.answer_key_text ?? "",
          grade: fresh.grade ?? "",
          type: fresh.type ?? "",
          dueDate: fresh.due_date ? String(fresh.due_date).slice(0, 10) : "",
        });
      } catch {
        // list copy is enough if detail fetch fails
      }
    })();
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingAssignment(null);
  };

  const confirmDeleteAssignment = useCallback(async () => {
    const a = deleteConfirmAssignment;
    if (!a) return;
    setDeleteLoading(true);
    try {
      await deleteAssignment(a.assignment_id);
      showSuccessToast("Assignment deleted.");
      setDeleteConfirmAssignment(null);
      if (editingAssignment?.assignment_id === a.assignment_id) {
        setEditModalOpen(false);
        setEditingAssignment(null);
      }
      if (detailAssignment?.assignment_id === a.assignment_id) {
        setDetailAssignment(null);
      }
      if (assigningAssignment?.assignment_id === a.assignment_id) {
        setAssigningAssignment(null);
      }
      if (createdAssignmentId === a.assignment_id) {
        setCreatedAssignmentId(null);
        setCreatePhase(1);
      }
      setAssignmentIdsWithTargets((prev) => {
        const next = new Set(prev);
        next.delete(a.assignment_id);
        return next;
      });
      await loadAssignments();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to delete assignment.");
    } finally {
      setDeleteLoading(false);
    }
  }, [
    deleteConfirmAssignment,
    editingAssignment,
    detailAssignment,
    assigningAssignment,
    createdAssignmentId,
    loadAssignments,
  ]);

  const handlePendingTeacherFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;
    setPendingTeacherAttachments((prev) => [...prev, ...picked].slice(0, 15));
  };

  const handleEditTeacherAttachmentInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editingAssignment) return;
    setTeacherAttachmentBusy(true);
    try {
      const row = await uploadAssignmentAttachment(editingAssignment.assignment_id, file);
      setEditingAssignment((prev) =>
        prev
          ? { ...prev, attachments: [...(prev.attachments || []), row] }
          : prev
      );
      showSuccessToast("File uploaded.");
      await loadAssignments();
      if (detailAssignment?.assignment_id === editingAssignment.assignment_id) {
        setDetailAssignment((d) =>
          d ? { ...d, attachments: [...(d.attachments || []), row] } : d
        );
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setTeacherAttachmentBusy(false);
    }
  };

  const handleDeleteTeacherAttachment = async (attachmentId: string) => {
    if (!editingAssignment) return;
    setTeacherAttachmentBusy(true);
    try {
      await deleteAssignmentAttachment(editingAssignment.assignment_id, attachmentId);
      setEditingAssignment((prev) =>
        prev
          ? {
              ...prev,
              attachments: (prev.attachments || []).filter((a) => a.attachment_id !== attachmentId),
            }
          : prev
      );
      showSuccessToast("File removed.");
      await loadAssignments();
      if (detailAssignment?.assignment_id === editingAssignment.assignment_id) {
        setDetailAssignment((d) =>
          d
            ? {
                ...d,
                attachments: (d.attachments || []).filter((a) => a.attachment_id !== attachmentId),
              }
            : d
        );
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to remove file.");
    } finally {
      setTeacherAttachmentBusy(false);
    }
  };

  const handleAnswerKeyFileInputCreate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setPendingAnswerKeyFile(file ?? null);
  };

  const handleUploadAnswerKeyEdit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editingAssignment) return;
    setAnswerKeyAttachmentBusy(true);
    try {
      const row = await uploadAnswerKeyAttachment(editingAssignment.assignment_id, file);
      setEditingAssignment((prev) => (prev ? { ...prev, answer_key_attachment: row } : prev));
      showSuccessToast("Answer key uploaded.");
      await loadAssignments();
      if (detailAssignment?.assignment_id === editingAssignment.assignment_id) {
        setDetailAssignment((d) => (d ? { ...d, answer_key_attachment: row } : d));
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setAnswerKeyAttachmentBusy(false);
    }
  };

  const handleDeleteAnswerKeyEdit = async () => {
    if (!editingAssignment) return;
    setAnswerKeyAttachmentBusy(true);
    try {
      await deleteAnswerKeyAttachment(editingAssignment.assignment_id);
      setEditingAssignment((prev) => (prev ? { ...prev, answer_key_attachment: null } : prev));
      showSuccessToast("Answer key file removed.");
      await loadAssignments();
      if (detailAssignment?.assignment_id === editingAssignment.assignment_id) {
        setDetailAssignment((d) => (d ? { ...d, answer_key_attachment: null } : d));
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to remove answer key file.");
    } finally {
      setAnswerKeyAttachmentBusy(false);
    }
  };

  const handleUpdateAssignment = async () => {
    if (!editingAssignment) return;
    if (!createForm.title.trim()) {
      showErrorToast("Please enter a title.");
      return;
    }
    if (createForm.dueDate.trim()) {
      const d = new Date(createForm.dueDate);
      if (Number.isNaN(d.getTime())) {
        showErrorToast("Please enter a valid due date or leave it empty.");
        return;
      }
    }
    setUpdateLoading(true);
    try {
      const payload = {
        title: createForm.title.trim(),
        title_tamil: createForm.titleTamil?.trim() || undefined,
        instructions: createForm.studentInstructions?.trim() || undefined,
        ai_instructions: createForm.aiInstructions?.trim() || undefined,
        answer_key_text: createForm.answerKeyText?.trim() || undefined,
        grade: createForm.grade || undefined,
        type: createForm.type || undefined,
        due_date: createForm.dueDate.trim() ? createForm.dueDate : undefined,
      };
      const updated = await updateAssignment(editingAssignment.assignment_id, payload);
      showSuccessToast("Assignment updated.");
      closeEditModal();
      if (detailAssignment?.assignment_id === editingAssignment.assignment_id) {
        setDetailAssignment(updated);
      }
      await loadAssignments();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to update assignment.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const openEditTargetModal = (target: AssignmentTargetDTO) => {
    if (target.target_type !== "students") return;
    setEditingTarget(target);
    setEditTargetStudentIds(
      new Set((target.students || []).map((s) => s.platform_user_id).filter(Boolean))
    );
    setEditTargetModalOpen(true);
    loadStudents();
  };

  const closeEditTargetModal = () => {
    setEditTargetModalOpen(false);
    setEditingTarget(null);
    setEditTargetStudentIds(new Set());
  };

  const handleUpdateTarget = async () => {
    if (!detailAssignment || !editingTarget) return;
    if (editTargetStudentIds.size === 0) {
      showErrorToast("Select at least one student.");
      return;
    }
    const students = Array.from(editTargetStudentIds).map((platform_user_id) => ({
      platform_user_id,
    }));
    setUpdateTargetLoading(true);
    try {
      await updateTarget(detailAssignment.assignment_id, editingTarget.target_id, { students });
      showSuccessToast("Target updated.");
      closeEditTargetModal();
      const res = await listTargets(detailAssignment.assignment_id);
      setDetailTargets(res.items);
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : "Failed to update target. Target may have been removed."
      );
    } finally {
      setUpdateTargetLoading(false);
    }
  };

  const handleRemoveTarget = async (target: AssignmentTargetDTO) => {
    if (!detailAssignment) return;
    if (!window.confirm("Remove this target from the assignment?")) return;
    try {
      await deleteTarget(detailAssignment.assignment_id, target.target_id);
      showSuccessToast("Target removed.");
      const res = await listTargets(detailAssignment.assignment_id);
      setDetailTargets(res.items);
      if (res.items.length === 0) {
        setAssignmentIdsWithTargets((prev) => {
          const next = new Set(prev);
          next.delete(detailAssignment.assignment_id);
          return next;
        });
      }
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : "Failed to remove target."
      );
    }
  };

  const handleCreateAssignment = async () => {
    if (!createForm.title.trim()) {
      showErrorToast("Please enter a title.");
      return;
    }
    if (createForm.dueDate.trim()) {
      const d = new Date(createForm.dueDate);
      if (Number.isNaN(d.getTime())) {
        showErrorToast("Please enter a valid due date or leave it empty.");
        return;
      }
    }
    setCreateLoading(true);
    try {
      const res = await createAssignment({
        title: createForm.title,
        title_tamil: createForm.titleTamil || undefined,
        instructions: createForm.studentInstructions || undefined,
        ai_instructions: createForm.aiInstructions?.trim() || undefined,
        answer_key_text: createForm.answerKeyText?.trim() || undefined,
        grade: createForm.grade || createFormGradeOptions.find((o) => o.value)?.value || "1",
        type: createForm.type || "Writing",
        due_date: createForm.dueDate.trim() ? createForm.dueDate : undefined,
      });
      if (pendingTeacherAttachments.length > 0) {
        for (const file of pendingTeacherAttachments) {
          await uploadAssignmentAttachment(res.assignment_id, file);
        }
        setPendingTeacherAttachments([]);
      }
      if (pendingAnswerKeyFile) {
        await uploadAnswerKeyAttachment(res.assignment_id, pendingAnswerKeyFile);
        setPendingAnswerKeyFile(null);
      }
      setCreatedAssignmentId(res.assignment_id);
      setCreatePhase(2);
      showSuccessToast("Assignment created. You can now assign targets.");
      await loadAssignments();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to create assignment.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreatePhase2Done = () => {
    closeCreateModal();
    loadAssignments();
  };

  const openDetailDrawer = useCallback(
    async (assignment: AssignmentDTO) => {
      setDetailAssignment(assignment);
      setDetailTargetsLoading(true);
      try {
        const res = await listTargets(assignment.assignment_id);
        setDetailTargets(res.items);
        if (res.items.length > 0) {
          setAssignmentIdsWithTargets((prev) => new Set([...prev, assignment.assignment_id]));
        }
      } catch {
        setDetailTargets([]);
        showErrorToast("Failed to load targets.");
      } finally {
        setDetailTargetsLoading(false);
      }
      try {
        const fresh = await getAssignment(assignment.assignment_id);
        setDetailAssignment(fresh);
      } catch {
        // keep list row snapshot if detail fetch fails
      }
    },
    []
  );

  const closeDetailDrawer = () => {
    setDetailAssignment(null);
    setDetailTargets([]);
  };

  const handleExportCsv = () => {
    const headers = ["Assignment", "Type", "Status", "Due Date"];
    const rows = filteredAssignments.map((a) =>
      [a.title, a.type, a.status, formatDueDate(a.due_date)].join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "assignments-track.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const resolveClassName = (classId: string | null | undefined) => {
    if (!classId) return "—";
    return classById.get(classId)?.name ?? classId;
  };

  const openCreateTopicModal = () => {
    if (classes.length === 0) return;
    setTopicForm({ title: "", description: "", section_id: "", grade: "2" });
    setCreateTopicModalOpen(true);
  };

  const closeCreateTopicModal = () => {
    setCreateTopicModalOpen(false);
  };

  const handleCreateTopic = async () => {
    if (!topicForm.title.trim()) {
      showErrorToast("Please enter a topic title.");
      return;
    }
    setCreateTopicLoading(true);
    try {
      await createTopic({
        title: topicForm.title,
        description: topicForm.description || undefined,
        section_id: topicForm.section_id || undefined,
        grade: topicForm.grade || "2",
      });
      showSuccessToast("Topic created successfully. Students will see it in Communication Tutor.");
      closeCreateTopicModal();
      loadTopics();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to create topic.");
    } finally {
      setCreateTopicLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <PageHeader title="Assignments" subtitle="பணிகளை நிர்வகி" />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {viewMode === "topics" && (
              <Button
                type="button"
                onClick={openCreateTopicModal}
                disabled={!hasClasses}
                className="bg-primary-600 hover:bg-primary-700 !text-white inline-flex items-center gap-2"
                title={hasClasses ? "Create Topic" : CREATE_TOPIC_DISABLED_TOOLTIP}
              >
                <MessageCircle className="w-4 h-4" />
                Create Topic
              </Button>
            )}
            {viewMode === "browse" && (
              <>
                <div className="w-full min-w-0 sm:w-48 flex-shrink-0">
                  <Select
                    options={gradeOptions}
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  onClick={openCreateModal}
                  disabled={!hasClasses}
                  className="h-10 w-10 p-0 rounded-lg inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 !text-white"
                  title={hasClasses ? "Create Assignment" : CREATE_DISABLED_TOOLTIP}
                  aria-label="Create Assignment"
                >
                  <PlusCircle className="w-4 h-4 flex-shrink-0" />
                </Button>
              </>
            )}
            {viewMode === "track" && (
              <Button
                type="button"
                onClick={handleExportCsv}
                variant="outline"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4 flex-shrink-0" />
                <span>Export CSV</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Button
              type="button"
              onClick={() => setViewMode("browse")}
              className={clsx(
                "inline-flex items-center justify-center gap-2 min-w-0 flex-1 sm:flex-initial",
                viewMode === "browse" && "bg-primary-600 hover:bg-primary-700 !text-white"
              )}
              variant="outline"
            >
              <LayoutGrid className="w-4 h-4 flex-shrink-0" />
              Browse
            </Button>
            <Button
              type="button"
              onClick={() => setViewMode("track")}
              className={clsx(
                "inline-flex items-center justify-center gap-2 min-w-0 flex-1 sm:flex-initial",
                viewMode === "track" && "bg-primary-600 hover:bg-primary-700 !text-white"
              )}
              variant="outline"
            >
              <List className="w-4 h-4 flex-shrink-0" />
              Track
            </Button>
            <Button
              type="button"
              onClick={() => setViewMode("topics")}
              className={clsx(
                "inline-flex items-center justify-center gap-2 min-w-0 flex-1 sm:flex-initial",
                viewMode === "topics" && "bg-primary-600 hover:bg-primary-700 !text-white"
              )}
              variant="outline"
            >
              <MessageCircle className="w-4 h-4 flex-shrink-0" />
              Communication
            </Button>
            <Button
              type="button"
              onClick={() => setViewMode("recent")}
              className={clsx(
                "inline-flex items-center justify-center gap-2 min-w-0 flex-1 sm:flex-initial",
                viewMode === "recent" && "bg-primary-600 hover:bg-primary-700 !text-white"
              )}
              variant="outline"
            >
              <History className="w-4 h-4 flex-shrink-0" />
              Recent Assignments
            </Button>
          </div>
        </div>

        {viewMode === "browse" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                <div className="col-span-full text-center py-12 text-slate-600 dark:text-slate-400">
                  Loading assignments...
                </div>
              ) : (
                filteredAssignments.map((assignment) => (
                  <Card
                    key={assignment.assignment_id}
                    padding="none"
                    className="bg-white border border-slate-200 shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    <CardContent padding="md">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Library className="w-7 h-7 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                            {assignment.title}
                          </h3>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {assignment.title_tamil || assignment.title}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              Grade {assignment.grade}
                            </Badge>
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {assignment.type}
                            </Badge>
                          </div>
                          {assignment.instructions && (
                            <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                                Student instructions
                              </p>
                              <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-2">
                                {assignment.instructions}
                              </p>
                            </div>
                          )}
                          {(assignment.ai_instructions?.trim() ||
                            assignment.answer_key_text?.trim() ||
                            assignment.answer_key_attachment) && (
                            <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-2 flex items-center gap-1">
                              <KeyRound className="w-3.5 h-3.5 flex-shrink-0" />
                              Staff: AI instructions / answer key on file
                            </p>
                          )}
                          {(assignment.attachments?.length ?? 0) > 0 && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                              {assignment.attachments!.length} material file
                              {assignment.attachments!.length === 1 ? "" : "s"} attached
                            </p>
                          )}
                          <div className="mt-4 flex items-center gap-2">
                              <Button
                                type="button"
                                onClick={() => openAssignModal(assignment)}
                                disabled={assignmentIdsWithTargets.has(assignment.assignment_id)}
                                className="h-10 w-10 p-0 rounded-lg inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 !text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                title={
                                  assignmentIdsWithTargets.has(assignment.assignment_id)
                                    ? "Already assigned"
                                    : "Assign to Class / Students"
                                }
                                aria-label="Assign to Class / Students"
                              >
                                <UserPlus className="w-4 h-4 flex-shrink-0" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => openDetailDrawer(assignment)}
                                className="h-10 w-10 p-0 rounded-lg inline-flex items-center justify-center"
                                title="View details"
                                aria-label="View details"
                              >
                                <Eye className="w-4 h-4 flex-shrink-0" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => openEditModal(assignment)}
                                className="h-10 w-10 p-0 rounded-lg inline-flex items-center justify-center"
                                title="Edit"
                                aria-label="Edit assignment"
                              >
                                <Pencil className="w-4 h-4 flex-shrink-0" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDeleteConfirmAssignment(assignment)}
                                className="h-10 w-10 p-0 rounded-lg inline-flex items-center justify-center text-red-600 hover:text-red-700 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Delete assignment"
                                aria-label="Delete assignment"
                              >
                                <Trash2 className="w-4 h-4 flex-shrink-0" />
                              </Button>
                            </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {!loading && filteredAssignments.length === 0 && (
              <div className="text-center py-16 text-slate-600 dark:text-slate-400">
                {assignments.length === 0
                  ? "No assignments yet. Create an assignment to get started."
                  : "No assignments match the selected grade."}
              </div>
            )}
          </>
        )}

        {viewMode === "topics" && (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Communication work in one place: <strong>topics</strong> below are for the Communication Tutor (section
              roster). Scroll down to create <strong>practice tasks with due dates</strong> tied to{" "}
              <Link to={ROUTES.TEACHER_CAN_DO} className="text-primary-600 font-semibold underline">
                Lesson Can Do → Communication
              </Link>{" "}
              topics and can-dos—students see those on My Assignments and in Communication Tutor.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topicsLoading ? (
                <div className="col-span-full text-center py-12 text-slate-600 dark:text-slate-400">
                  Loading topics...
                </div>
              ) : (
                topics.map((topic) => (
                  <Card
                    key={topic.topic_id}
                    padding="none"
                    className="bg-white border border-slate-200 shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    <CardContent padding="md">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-7 h-7 text-indigo-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-slate-900">{topic.title}</h3>
                          {topic.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                              {topic.description}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                              Grade {topic.grade}
                            </Badge>
                            <Badge variant="gray" className="text-slate-600">
                              {sections.find((s) => s.section.id === topic.section_id)?.section.name ??
                                topic.section_id ??
                                "—"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
            {!topicsLoading && topics.length === 0 && (
              <div className="text-center py-16 text-slate-600 dark:text-slate-400">
                No topics yet. Create a topic to get started.
              </div>
            )}

            <div
              id="teacher-communication-practice"
              className="mt-12 pt-10 border-t border-slate-200 dark:border-slate-700 scroll-mt-4"
            >
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Practice tasks (due dates)</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Assign dated communication practice. Pick the same topics and can-dos you maintain in Lesson Can Do.
              </p>
              <TeacherCommunicationPracticeTasksPanel
                gradeSelectOptions={createFormGradeOptions}
                sectionSelectOptions={sectionOptions}
              />
            </div>
          </>
        )}

        {viewMode === "track" && (
          <>
            {loading ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Loading assignments...
              </div>
            ) : filteredAssignments.length === 0 ? (
              <div className="text-center py-16 text-slate-600 dark:text-slate-400">
                {assignments.length === 0
                  ? "No assignments to track."
                  : "No assignments match the selected grade."}
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <Card className="overflow-hidden border border-slate-200 dark:border-slate-700 min-w-[640px]" padding="none">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-primary-600 dark:bg-primary-700">
                        <TableHead className="text-white font-semibold">Assignment</TableHead>
                        <TableHead className="text-white font-semibold">Type</TableHead>
                        <TableHead className="text-white font-semibold">Targets</TableHead>
                        <TableHead className="text-white font-semibold">Status</TableHead>
                        <TableHead className="text-white font-semibold">Due Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAssignments.map((item) => (
                        <TableRow key={item.assignment_id}>
                          <TableCell>
                            <div className="min-w-0">
                              <span className="font-medium text-slate-900 dark:text-white block truncate">
                                {item.title}
                              </span>
                              <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                                {item.title_tamil || item.title}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700 dark:text-slate-300">
                            {item.type}
                          </TableCell>
                          <TableCell className="text-slate-700 dark:text-slate-300">
                            <span className="inline-flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => openDetailDrawer(item)}
                                className="inline-flex items-center gap-1.5"
                              >
                                <Eye className="w-4 h-4 flex-shrink-0" />
                                View details
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => openEditModal(item)}
                                className="inline-flex items-center gap-1.5"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4 flex-shrink-0" />
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteConfirmAssignment(item)}
                                className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Delete assignment"
                              >
                                <Trash2 className="w-4 h-4 flex-shrink-0" />
                                Delete
                              </Button>
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                              <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" />
                              {item.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-700 dark:text-slate-300">
                            {formatDueDate(item.due_date)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}
          </>
        )}

        {viewMode === "recent" && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="w-full sm:w-48">
                <Select
                  options={recentSubmissionGradeOptions}
                  value={recentGradeFilter}
                  onChange={(e) => setRecentGradeFilter(e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => loadRecentSubmissions()}
                disabled={loadingRecentSubmissions}
                className="inline-flex items-center justify-center gap-2 flex-shrink-0"
              >
                <RefreshCw className={loadingRecentSubmissions ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
                Refresh
              </Button>
            </div>
            {loadingRecentSubmissions ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Loading recent submissions...
              </div>
            ) : filteredRecentSubmissions.length === 0 ? (
              <div className="text-center py-16 text-slate-600 dark:text-slate-400">
                No recent submissions yet.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredRecentSubmissions.map((sub) => {
                  const aiPractice = pickValidationStudentScore(sub.validation_student_summary);
                  const validationDone = sub.validation_status?.toLowerCase() === "completed";
                  const ocrFailed = Boolean(sub.ocr_error && String(sub.ocr_error).trim());
                  const ocrLooksReal =
                    sub.ocr_score != null &&
                    !ocrFailed &&
                    (Boolean(sub.ocr_text?.trim()) ||
                      (sub.validation_staff != null && Object.keys(sub.validation_staff).length > 0) ||
                      (sub.ocr_score ?? 0) > 0);

                  return (
                  <div
                    key={sub.submission_id}
                    className="flex flex-wrap items-center justify-between gap-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-700 dark:text-blue-300" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">
                          {sub.assignment_title || sub.assignment_id}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Student: {studentNameMap.get(sub.student_id) ?? sub.student_id}
                          {sub.grade_level && (
                            <span className="ml-2">· Grade {sub.grade_level}</span>
                          )}
                          {validationDone && aiPractice != null && (
                            <span className="ml-2">· AI check: {aiPractice}/100</span>
                          )}
                          {!(validationDone && aiPractice != null) && ocrLooksReal && (
                            <span className="ml-2">· OCR: {sub.ocr_score}/100</span>
                          )}
                          {sub.submitted_at && (
                            <span className="ml-2">
                              · Submitted:{" "}
                              {new Date(sub.submitted_at).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge
                        variant={
                          sub.status === "published"
                            ? "success"
                            : sub.status === "submitted"
                              ? "warning"
                              : "gray"
                        }
                      >
                        {sub.status}
                      </Badge>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(teacherSubmissionReviewPath(sub.submission_id), {
                            state: {
                              from: "assignments",
                              restoreAssignmentsView: "recent",
                              seedSubmission: {
                                ...sub,
                                student_display_name:
                                  sub.student_display_name?.trim() ||
                                  studentNameMap.get(sub.student_id) ||
                                  undefined,
                              },
                            },
                          })
                        }
                        className="inline-flex items-center gap-1.5"
                      >
                        <Eye className="w-4 h-4 flex-shrink-0" />
                        Review
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Assign modal (existing assignment or after create phase 2) */}
        <Modal
          isOpen={!!assigningAssignment || (createPhase === 2 && !!createdAssignmentId)}
          onClose={createPhase === 2 ? handleCreatePhase2Done : closeAssignModal}
          title={
            createPhase === 2
              ? "Assign To"
              : assigningAssignment
                ? `Assign: ${assigningAssignment.title_tamil || assigningAssignment.title}`
                : ""
          }
          size="lg"
          showCloseButton
        >
          {(assigningAssignment || (createPhase === 2 && createdAssignmentId)) && (
            <div className="space-y-6">
              {createPhase === 2 && (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Assignment created. Add targets below, or click Done to finish.
                </p>
              )}
              {assigningAssignment && (
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-blue-700 dark:text-blue-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-white">
                        {assigningAssignment.title}
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {assigningAssignment.title_tamil}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Assign To
                </p>
                <div className="flex flex-wrap gap-2">
                  {(["class", "students"] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setAssignTab(tab)}
                      className={clsx(
                        "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors flex-1 min-w-0 sm:flex-initial",
                        assignTab === tab
                          ? "bg-primary-600 text-white"
                          : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600"
                      )}
                    >
                      {tab === "class" ? <Users className="w-4 h-4 flex-shrink-0" /> : <UserPlus className="w-4 h-4 flex-shrink-0" />}
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              {assignTab === "class" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Select a class
                  </label>
                  <Select
                    options={classOptions}
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  />
                </div>
              )}

              {assignTab === "students" && (
                <div className="space-y-4">
                  {!students.length && !studentsLoading && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Load students to select.
                    </p>
                  )}
                  {studentsLoading && (
                    <p className="text-sm text-slate-500">Loading students...</p>
                  )}
                  {students.length > 0 && (
                    <>
                      {!isAdmin && classes.length > 1 && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Filter by class
                          </label>
                          <Select
                            options={[
                              { value: "", label: "All classes" },
                              ...classOptions.filter((o) => o.value),
                            ]}
                            value={studentClassFilter}
                            onChange={(e) => setStudentClassFilter(e.target.value)}
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Search students
                        </label>
                        <Input
                          type="text"
                          placeholder="Search by name or admission no..."
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Select students
                        </label>
                        <div
                          className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg p-2 space-y-1"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {filteredStudents.slice(0, 100).map((entry) => {
                            const pid = entry.student.platform_user_id;
                            if (!pid) return null;
                            const checked = selectedStudentIds.has(pid);
                            return (
                              <label
                                key={pid}
                                className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    setSelectedStudentIds((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(pid)) next.delete(pid);
                                      else next.add(pid);
                                      return next;
                                    });
                                  }}
                                  className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-slate-700 dark:text-slate-300">
                                  {entry.student.display_name ||
                                    [entry.student.first_name, entry.student.last_name]
                                      .filter(Boolean)
                                      .join(" ") ||
                                    entry.student.admission_no ||
                                    pid}
                                </span>
                                {entry.class?.name && (
                                  <span className="text-xs text-slate-500">({entry.class.name})</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                        {filteredStudents.length > 100 && (
                          <p className="text-xs text-slate-500 mt-1">
                            Showing first 100. Narrow search to see more.
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Button
                  variant="outline"
                  onClick={createPhase === 2 ? handleCreatePhase2Done : closeAssignModal}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2"
                >
                  {createPhase === 2 ? <Check className="w-4 h-4 flex-shrink-0" /> : <X className="w-4 h-4 flex-shrink-0" />}
                  {createPhase === 2 ? "Done" : "Cancel"}
                </Button>
                <Button
                  onClick={handleAssignNow}
                  disabled={assignLoading}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 !text-white"
                >
                  <Target className="w-4 h-4 flex-shrink-0" />
                  {assignLoading ? "Assigning..." : "Add Target"}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Assignment details drawer (modal) */}
        <Modal
          isOpen={!!detailAssignment}
          onClose={closeDetailDrawer}
          title={detailAssignment ? `Targets: ${detailAssignment.title_tamil || detailAssignment.title}` : ""}
          size="lg"
          showCloseButton
        >
          {detailAssignment && (
            <div className="space-y-6">
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <h4 className="font-bold text-slate-900 dark:text-white">{detailAssignment.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Grade {detailAssignment.grade} · {detailAssignment.type}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Due: {formatDueDate(detailAssignment.due_date)}
                </p>
                {detailAssignment.instructions && (
                  <div className="mt-3 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {detailAssignment.instructions}
                  </div>
                )}
                {(detailAssignment.ai_instructions?.trim() ||
                  detailAssignment.answer_key_text?.trim() ||
                  detailAssignment.answer_key_attachment) && (
                  <div className="mt-4 p-3 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 space-y-2">
                    <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide flex items-center gap-1">
                      <KeyRound className="w-3.5 h-3.5" />
                      Staff only (not shown to students)
                    </p>
                    {detailAssignment.ai_instructions?.trim() && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                          AI instructions
                        </p>
                        <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                          {detailAssignment.ai_instructions}
                        </p>
                      </div>
                    )}
                    {detailAssignment.answer_key_text?.trim() && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                          Answer key (text)
                        </p>
                        <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                          {detailAssignment.answer_key_text}
                        </p>
                      </div>
                    )}
                    {detailAssignment.answer_key_attachment && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                          Answer key (file)
                        </p>
                        {detailAssignment.answer_key_attachment.url ? (
                          <a
                            href={detailAssignment.answer_key_attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:underline break-all"
                          >
                            {detailAssignment.answer_key_attachment.filename}
                          </a>
                        ) : detailAssignment.answer_key_attachment.fetch_path ? (
                          <button
                            type="button"
                            onClick={() =>
                              void openAssignmentAttachmentFile(detailAssignment.answer_key_attachment!)
                            }
                            className="text-sm text-primary-600 hover:underline break-all text-left"
                          >
                            {detailAssignment.answer_key_attachment.filename}
                          </button>
                        ) : (
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {detailAssignment.answer_key_attachment.filename}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {(detailAssignment.attachments?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                      Materials for students
                    </p>
                    <ul className="space-y-1">
                      {detailAssignment.attachments!.map((a) => (
                        <li key={a.attachment_id}>
                          {a.url ? (
                            <a
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-600 hover:underline break-all"
                            >
                              {a.filename}
                            </a>
                          ) : a.fetch_path ? (
                            <button
                              type="button"
                              onClick={() => void openAssignmentAttachmentFile(a)}
                              className="text-sm text-primary-600 hover:underline break-all text-left"
                            >
                              {a.filename}
                            </button>
                          ) : (
                            <span className="text-sm text-slate-600 dark:text-slate-400">{a.filename}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {detailTargetsLoading ? (
                <p className="text-sm text-slate-500">Loading targets...</p>
              ) : detailTargets.length === 0 ? (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  No targets yet. Add a class or students below.
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Current targets
                  </p>
                  <ul className="space-y-2">
                    {detailTargets.map((t) => (
                      <li
                        key={t.target_id}
                        className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
                      >
                        {t.target_type === "class" ? (
                          <>
                            <Users className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            <span className="text-sm text-slate-700 dark:text-slate-300 min-w-0 flex-1">
                              Class: {t.class_name || resolveClassName(t.class_id) || t.class_id || "—"}
                            </span>
                          </>
                        ) : (
                          <>
                            <Users className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            <span className="text-sm text-slate-700 dark:text-slate-300 min-w-0 flex-1 truncate">
                              Students:{" "}
                              {(t.students && t.students.length > 0)
                                ? t.students
                                    .map((s) => s.display_name || s.platform_user_id)
                                    .join(", ")
                                : "—"}
                              {t.students && t.students.length > 1 && (
                                <span className="text-slate-500"> ({t.students.length})</span>
                              )}
                            </span>
                          </>
                        )}
                        <span className="text-xs text-slate-500 flex-shrink-0 w-full sm:w-auto sm:ml-auto order-last sm:order-none w-full sm:w-auto">
                          Due: {formatDueDate(t.due_date ?? detailAssignment.due_date)}
                        </span>
                        <span className="inline-flex items-center gap-1 flex-shrink-0">
                          {t.target_type === "students" && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openEditTargetModal(t)}
                              className="h-8 px-2"
                              title="Edit students"
                              aria-label="Edit students"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveTarget(t)}
                            className="h-8 px-2 text-red-600 hover:text-red-700 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Remove target"
                            aria-label="Remove target"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row flex-wrap gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Button
                  variant="outline"
                  onClick={closeDetailDrawer}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4 flex-shrink-0" />
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openEditModal(detailAssignment)}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2"
                >
                  <Pencil className="w-4 h-4 flex-shrink-0" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmAssignment(detailAssignment)}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 text-red-600 hover:text-red-700 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                  Delete
                </Button>
                <Button
                  onClick={() => openAssignModal(detailAssignment)}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 !text-white"
                >
                  <Target className="w-4 h-4 flex-shrink-0" />
                  Add Target
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Edit target modal (student target only) */}
        <Modal
          isOpen={editTargetModalOpen && !!editingTarget}
          onClose={closeEditTargetModal}
          title="Edit target – Students"
          size="lg"
          showCloseButton
        >
          {editingTarget && (
            <div className="space-y-6">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Change which students are in this target. At least one student is required.
              </p>
              {!students.length && !studentsLoading && (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Load students to select.
                </p>
              )}
              {studentsLoading && (
                <p className="text-sm text-slate-500">Loading students...</p>
              )}
              {students.length > 0 && (
                <>
                  {!isAdmin && classes.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Filter by class
                      </label>
                      <Select
                        options={[
                          { value: "", label: "All classes" },
                          ...classOptions.filter((o) => o.value),
                        ]}
                        value={studentClassFilter}
                        onChange={(e) => setStudentClassFilter(e.target.value)}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Search students
                    </label>
                    <Input
                      type="text"
                      placeholder="Search by name or admission no..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Select students
                    </label>
                    <div
                      className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg p-2 space-y-1"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {filteredStudents.slice(0, 100).map((entry) => {
                        const pid = entry.student.platform_user_id;
                        if (!pid) return null;
                        const checked = editTargetStudentIds.has(pid);
                        return (
                          <label
                            key={pid}
                            className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setEditTargetStudentIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(pid)) next.delete(pid);
                                  else next.add(pid);
                                  return next;
                                });
                              }}
                              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">
                              {entry.student.display_name ||
                                [entry.student.first_name, entry.student.last_name]
                                  .filter(Boolean)
                                  .join(" ") ||
                                entry.student.admission_no ||
                                pid}
                            </span>
                            {entry.class?.name && (
                              <span className="text-xs text-slate-500">({entry.class.name})</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    {filteredStudents.length > 100 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Showing first 100. Narrow search to see more.
                      </p>
                    )}
                  </div>
                </>
              )}
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Button
                  variant="outline"
                  onClick={closeEditTargetModal}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4 flex-shrink-0" />
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateTarget}
                  disabled={updateTargetLoading || editTargetStudentIds.size === 0}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 !text-white"
                >
                  <Check className="w-4 h-4 flex-shrink-0" />
                  {updateTargetLoading ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Create / Edit assignment modal */}
        <Modal
          isOpen={(createModalOpen && createPhase === 1) || (editModalOpen && !!editingAssignment)}
          onClose={editingAssignment ? closeEditModal : closeCreateModal}
          title={editingAssignment ? "Edit Assignment" : "Create Assignment"}
          size="lg"
          showCloseButton
        >
          <div className="space-y-6">
            {/* title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Title
              </label>
              <Input
                placeholder="e.g. Tamil Writing Exercise 1"
                value={createForm.title}
                onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            {/* title_tamil */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Title (Tamil)
              </label>
              <Input
                placeholder="e.g. தமிழ் எழுத்து பயிற்சி 1"
                value={createForm.titleTamil}
                onChange={(e) => setCreateForm((f) => ({ ...f, titleTamil: e.target.value }))}
              />
            </div>

            {/* student instructions */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Student instructions
              </label>
              <Textarea
                rows={4}
                placeholder="e.g. Write a short paragraph about your family in Tamil."
                value={createForm.studentInstructions}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, studentInstructions: e.target.value }))
                }
              />
            </div>

            <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/80 dark:bg-amber-950/20 p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                <KeyRound className="w-4 h-4 flex-shrink-0" />
                Staff only (not shown to students)
              </div>
              <p className="text-xs text-amber-800/90 dark:text-amber-200/80">
                AI instructions and answer keys are stored for review or future auto-validation. Students only see
                student instructions and materials above.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  AI instructions
                </label>
                <Textarea
                  rows={3}
                  placeholder="e.g. Rubric, tone, or model guidance for internal use…"
                  value={createForm.aiInstructions}
                  onChange={(e) => setCreateForm((f) => ({ ...f, aiInstructions: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Answer key (text)
                </label>
                <Textarea
                  rows={3}
                  placeholder="Expected answers or rubric text…"
                  value={createForm.answerKeyText}
                  onChange={(e) => setCreateForm((f) => ({ ...f, answerKeyText: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Answer key (file)
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  One file (same types as materials). Replaces any previous answer key file.
                </p>
                {editingAssignment ? (
                  <div className="space-y-2">
                    {editingAssignment.answer_key_attachment && (
                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
                        {editingAssignment.answer_key_attachment.url ? (
                          <a
                            href={editingAssignment.answer_key_attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline min-w-0 flex-1 truncate"
                          >
                            {editingAssignment.answer_key_attachment.filename}
                          </a>
                        ) : editingAssignment.answer_key_attachment.fetch_path ? (
                          <button
                            type="button"
                            onClick={() =>
                              void openAssignmentAttachmentFile(editingAssignment.answer_key_attachment!)
                            }
                            className="text-primary-600 hover:underline min-w-0 flex-1 truncate text-left"
                          >
                            {editingAssignment.answer_key_attachment.filename}
                          </button>
                        ) : (
                          <span className="text-slate-700 dark:text-slate-300 min-w-0 flex-1 truncate">
                            {editingAssignment.answer_key_attachment.filename}
                          </span>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-red-600 border-red-200 dark:border-red-800"
                          disabled={answerKeyAttachmentBusy}
                          onClick={() => void handleDeleteAnswerKeyEdit()}
                          aria-label="Remove answer key file"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-500 cursor-pointer transition-colors text-sm text-slate-600 dark:text-slate-400">
                      <Upload className="w-4 h-4 flex-shrink-0" />
                      <span>{answerKeyAttachmentBusy ? "Uploading…" : "Upload / replace answer key"}</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept={ASSIGNMENT_RESOURCE_ACCEPT}
                        disabled={answerKeyAttachmentBusy}
                        onChange={(e) => void handleUploadAnswerKeyEdit(e)}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pendingAnswerKeyFile && (
                      <div className="flex items-center justify-between gap-2 text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2">
                        <span className="truncate min-w-0">{pendingAnswerKeyFile.name}</span>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 p-1 flex-shrink-0"
                          onClick={() => setPendingAnswerKeyFile(null)}
                          aria-label="Remove answer key file from list"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-500 cursor-pointer transition-colors text-sm text-slate-600 dark:text-slate-400">
                      <Upload className="w-4 h-4 flex-shrink-0" />
                      <span>Choose answer key file</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept={ASSIGNMENT_RESOURCE_ACCEPT}
                        onChange={handleAnswerKeyFileInputCreate}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Materials (optional)
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Video, audio, images, PDF, or documents (up to 15 files, 100 MB each). Students can open these from
                the assignment.
              </p>
              {editingAssignment ? (
                <div className="space-y-3">
                  {(editingAssignment.attachments?.length ?? 0) > 0 && (
                    <ul className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-700">
                      {editingAssignment.attachments!.map((a) => (
                        <li
                          key={a.attachment_id}
                          className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                        >
                          {a.url ? (
                            <a
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline min-w-0 flex-1 truncate"
                            >
                              {a.filename}
                            </a>
                          ) : a.fetch_path ? (
                            <button
                              type="button"
                              onClick={() => void openAssignmentAttachmentFile(a)}
                              className="text-primary-600 hover:underline min-w-0 flex-1 truncate text-left"
                            >
                              {a.filename}
                            </button>
                          ) : (
                            <span className="text-slate-700 dark:text-slate-300 min-w-0 flex-1 truncate">
                              {a.filename}
                            </span>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-red-600 border-red-200 dark:border-red-800"
                            disabled={teacherAttachmentBusy}
                            onClick={() => void handleDeleteTeacherAttachment(a.attachment_id)}
                            aria-label={`Remove ${a.filename}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-500 cursor-pointer transition-colors text-sm text-slate-600 dark:text-slate-400">
                    <Upload className="w-4 h-4 flex-shrink-0" />
                    <span>{teacherAttachmentBusy ? "Uploading…" : "Add file"}</span>
                    <input
                      type="file"
                      className="sr-only"
                      accept={ASSIGNMENT_RESOURCE_ACCEPT}
                      disabled={teacherAttachmentBusy}
                      onChange={(e) => void handleEditTeacherAttachmentInput(e)}
                    />
                  </label>
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingTeacherAttachments.map((f, idx) => (
                    <div
                      key={`${f.name}-${idx}`}
                      className="flex items-center justify-between gap-2 text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2"
                    >
                      <span className="truncate min-w-0">{f.name}</span>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-700 p-1 flex-shrink-0"
                        onClick={() =>
                          setPendingTeacherAttachments((prev) => prev.filter((_, j) => j !== idx))
                        }
                        aria-label="Remove file from list"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-500 cursor-pointer transition-colors text-sm text-slate-600 dark:text-slate-400">
                    <Upload className="w-4 h-4 flex-shrink-0" />
                    <span>Add files</span>
                    <input
                      type="file"
                      multiple
                      className="sr-only"
                      accept={ASSIGNMENT_RESOURCE_ACCEPT}
                      onChange={handlePendingTeacherFiles}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* grade */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Grade
                </label>
                <Select
                  options={createFormGradeOptions}
                  value={createForm.grade}
                  onChange={(e) => setCreateForm((f) => ({ ...f, grade: e.target.value }))}
                />
              </div>
              {/* type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Type
                </label>
                <Select
                  options={[{ value: "", label: "Select type..." }, ...TYPE_OPTIONS]}
                  value={createForm.type}
                  onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}
                />
              </div>
            </div>

            {/* due_date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Due Date
              </label>
              <Input
                type="date"
                value={createForm.dueDate}
                onChange={(e) => setCreateForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row flex-wrap gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={editingAssignment ? closeEditModal : closeCreateModal}
                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4 flex-shrink-0" />
                Cancel
              </Button>
              {editingAssignment ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteConfirmAssignment(editingAssignment)}
                    className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 text-red-600 hover:text-red-700 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                    Delete
                  </Button>
                  <Button
                    onClick={handleUpdateAssignment}
                    disabled={updateLoading || teacherAttachmentBusy}
                    className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 !text-white"
                  >
                    <Check className="w-4 h-4 flex-shrink-0" />
                    {updateLoading ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleCreateAssignment}
                  disabled={createLoading || teacherAttachmentBusy}
                  className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 !text-white"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  {createLoading ? "Creating..." : "Create"}
                </Button>
              )}
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={createTopicModalOpen}
          onClose={closeCreateTopicModal}
          title="Create Communication Topic"
          size="lg"
          showCloseButton
        >
          <div className="space-y-6">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Topics appear in the Communication Tutor for students in the selected section. Students choose a topic to practice Tamil conversation.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Topic Title
              </label>
              <Input
                placeholder="e.g. Family, School, Animals"
                value={topicForm.title}
                onChange={(e) => setTopicForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Description (Optional)
              </label>
              <Textarea
                rows={3}
                placeholder="Additional context for the AI tutor..."
                value={topicForm.description}
                onChange={(e) => setTopicForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Section (Optional)
              </label>
              <Select
                options={sectionOptions}
                value={topicForm.section_id}
                onChange={(e) => setTopicForm((f) => ({ ...f, section_id: e.target.value }))}
              />
            </div> */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Grade
              </label>
              <Select
                options={GRADE_OPTIONS}
                value={topicForm.grade}
                onChange={(e) => setTopicForm((f) => ({ ...f, grade: e.target.value }))}
              />
            </div>
            <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={closeCreateTopicModal} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreateTopic}
                disabled={createTopicLoading}
                className="flex-1 bg-primary-600 hover:bg-primary-700 !text-white"
              >
                {createTopicLoading ? "Creating..." : "Create Topic"}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={!!deleteConfirmAssignment}
          onClose={() => {
            if (!deleteLoading) setDeleteConfirmAssignment(null);
          }}
          title="Delete assignment?"
          size="md"
          showCloseButton={!deleteLoading}
        >
          {deleteConfirmAssignment && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium text-slate-900 dark:text-white">
                  {deleteConfirmAssignment.title_tamil || deleteConfirmAssignment.title}
                </span>{" "}
                will be removed from the assignment list. Targets for this assignment will be cancelled. Existing
                student submissions are not deleted from the server.
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={deleteLoading}
                  onClick={() => setDeleteConfirmAssignment(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="flex-1 bg-red-600 hover:bg-red-700 !text-white"
                  disabled={deleteLoading}
                  onClick={() => void confirmDeleteAssignment()}
                >
                  {deleteLoading ? "Deleting..." : "Delete assignment"}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
};

export default AssignmentsPage;
